import type { QueryClient } from '@tanstack/react-query';

import type { DialogDto, MessageDto, Page } from '../api/types';
import { wsClient } from './client';
import type { WsEvent } from './types';

/** Patches/invalidates TanStack Query caches as WS events arrive (spec §10.3). */
export function wireWsToQueryClient(queryClient: QueryClient): () => void {
  return wsClient.on((event: WsEvent) => {
    switch (event.t) {
      case 'dialog.updated':
      case 'handoff.changed':
      case 'sla.changed': {
        const dialogId = 'dialogId' in event ? event.dialogId : event.dialog.id;
        void queryClient.invalidateQueries({ queryKey: ['dialogs', 'list'] });
        void queryClient.invalidateQueries({ queryKey: ['dialogs', 'detail', dialogId] });
        break;
      }
      case 'message.created': {
        patchMessagesCache(queryClient, event.dialogId, event.message as unknown as MessageDto);
        void queryClient.invalidateQueries({ queryKey: ['dialogs', 'list'] });
        void queryClient.invalidateQueries({ queryKey: ['dialogs', 'detail', event.dialogId] });
        break;
      }
      case 'channel.status': {
        void queryClient.invalidateQueries({ queryKey: ['channels'] });
        void queryClient.invalidateQueries({ queryKey: ['dialogs', 'list'] });
        break;
      }
      default:
        break;
    }
  });
}

function patchMessagesCache(queryClient: QueryClient, dialogId: string, message: MessageDto): void {
  queryClient.setQueriesData<Page<MessageDto> | undefined>(
    { queryKey: ['dialogs', 'messages', dialogId] },
    (old) => (old ? withMessage(old, message) : old),
  );
}

function withMessage(page: Page<MessageDto>, message: MessageDto): Page<MessageDto> {
  if (page.items.some((m) => m.id === message.id)) return page;
  return { ...page, items: [message, ...page.items] };
}

export type { DialogDto };
